/* ============================================================================
 * SEO INTELLIGENCE ENGINE v2.0
 * ============================================================================
 * Pure JavaScript, no Vue dependency. Single responsibility: analyze website
 * internal linking structure semantically (not keyword count).
 *
 * Usage:
 *   import SeoEngine from './SeoEngine.js'
 *   const result = SeoEngine.analyze(items, stopWords, config)
 *   // result.hubs, .spokes, .orphans, .keywordGaps, .pageScores, etc.
 * ========================================================================== */

const SeoEngine = {
  /* =========================================================================
   * SECTION A — PERSIAN TEXT NORMALIZATION & PREPROCESSING
   * ======================================================================= */
  normalizePersian(text) {
    if (!text) return '';
    let t = String(text);
    t = t.replace(/ي/g, 'ی').replace(/ك/g, 'ک');
    t = t.replace(/[\u200c\u200b\u200e\u200f]/g, ' ');
    t = t.replace(/[أإآ]/g, 'ا').replace(/ة/g, 'ه').replace(/ؤ/g, 'و').replace(/ئ/g, 'ی');
    t = t.replace(/[.,،؛:!؟"'`«»()\[\]{}<>\/\\|+*=_%$#@^~]/g, ' ');
    t = t.replace(/\s+/g, ' ').trim();
    return t;
  },

  tokenize(text, stopWords) {
    const clean = this.normalizePersian(text);
    if (!clean) return [];
    const sw = stopWords || [];
    return clean.split(' ').map(w => w.trim()).filter(w => w.length > 1 && !sw.includes(w));
  },

  parseHtml(htmlContent) {
    const empty = { headings: { h2: [], h3: [], h4: [] }, paragraphs: [], listItems: [], anchors: [], images: [], tableCount: 0, listCount: 0, plainText: '' };
    if (!htmlContent || typeof DOMParser === 'undefined') return empty;
    let doc;
    try {
      doc = new DOMParser().parseFromString(htmlContent, 'text/html');
    } catch (e) {
      return empty;
    }
    doc.querySelectorAll('script, style').forEach(n => n.remove());
    const headings = {
      h2: Array.from(doc.querySelectorAll('h2')).map(n => n.textContent.trim()).filter(Boolean),
      h3: Array.from(doc.querySelectorAll('h3')).map(n => n.textContent.trim()).filter(Boolean),
      h4: Array.from(doc.querySelectorAll('h4')).map(n => n.textContent.trim()).filter(Boolean)
    };
    const paragraphs = Array.from(doc.querySelectorAll('p')).map(n => n.textContent.trim()).filter(Boolean);
    const listItems = Array.from(doc.querySelectorAll('li')).map(n => n.textContent.trim()).filter(Boolean);
    const anchors = Array.from(doc.querySelectorAll('a')).map(n => ({
      url: n.getAttribute('href') || '',
      text: n.textContent.trim()
    })).filter(a => a.url && a.url.includes('/'));
    const images = Array.from(doc.querySelectorAll('img')).map(n => ({
      src: n.getAttribute('src') || '',
      alt: n.getAttribute('alt') || ''
    }));
    const tableCount = doc.querySelectorAll('table').length;
    const listCount = doc.querySelectorAll('ul, ol').length;
    const plainText = (doc.body ? doc.body.textContent : '').replace(/\s+/g, ' ').trim();
    return { headings, paragraphs, listItems, anchors, images, tableCount, listCount, plainText };
  },

  /* =========================================================================
   * SECTION B — WEIGHTED TERM VECTORS
   * ======================================================================= */
  buildTermVector(weightedTexts, stopWords) {
    const vec = {};
    (weightedTexts || []).forEach(({ text, weight }) => {
      this.tokenize(text, stopWords).forEach(tok => {
        vec[tok] = (vec[tok] || 0) + weight;
      });
    });
    return vec;
  },

  vectorTopTerms(vec, n) {
    return Object.entries(vec)
      .sort((a, b) => b[1] - a[1])
      .slice(0, n || 25)
      .map(([term, weight]) => ({ term, weight }));
  },

  weightedCosine(vecA, vecB) {
    const keys = new Set([...Object.keys(vecA || {}), ...Object.keys(vecB || {})]);
    let dot = 0, magA = 0, magB = 0;
    keys.forEach(k => {
      const a = (vecA ? vecA[k] : 0) || 0;
      const b = (vecB ? vecB[k] : 0) || 0;
      dot += a * b;
      magA += a * a;
      magB += b * b;
    });
    if (magA === 0 || magB === 0) return 0;
    return dot / (Math.sqrt(magA) * Math.sqrt(magB));
  },

  jaccard(setA, setB) {
    const a = new Set(setA || []);
    const b = new Set(setB || []);
    if (a.size === 0 && b.size === 0) return 0;
    let inter = 0;
    a.forEach(v => { if (b.has(v)) inter++; });
    const union = new Set([...a, ...b]).size;
    return union === 0 ? 0 : inter / union;
  },

  /* =========================================================================
   * SECTION C — DEFAULT DICTIONARIES
   * ======================================================================= */
  defaultLocations: [
    'تهران', 'اصفهان', 'شیراز', 'مشهد', 'تبریز', 'کرج', 'اهواز', 'قم',
    'ارومیه', 'رشت', 'زاهدان', 'کرمان', 'یزد', 'شمال', 'جنوب'
  ],
  defaultIndustries: [
    'پزشکی', 'کلینیک', 'دندانپزشکی', 'فروشگاهی', 'شرکتی', 'وکالت',
    'رستوران', 'هتل', 'گردشگری', 'آموزشی', 'خودرو', 'مهندسی'
  ],
  defaultServiceSeeds: [
    'طراحی', 'سئو', 'بهینه‌سازی', 'محتوا', 'برنامه‌نویسی', 'توسعه'
  ],
  intentSignals: [
    { intent: 'price', words: ['قیمت', 'هزینه', 'تعرفه'] },
    { intent: 'comparison', words: ['مقایسه', 'بهتر', 'تفاوت'] },
    { intent: 'faq', words: ['سوالات', 'پرسش', 'متداول'] },
    { intent: 'guide', words: ['راهنما', 'آموزش', 'نحوه'] }
  ],
  industryGapDictionary: ['پزشکی', 'فروشگاهی', 'شرکتی', 'دندانپزشکی', 'رستوران'],
  clusterKeywordMap: [
    { cluster: 'طراحی سایت', words: ['طراحی', 'سایت', 'وب‌سایت'] },
    { cluster: 'سئو', words: ['سئو', 'بهینه‌سازی'] },
    { cluster: 'پزشکی', words: ['پزشکی', 'کلینیک', 'دندانپزشکی'] },
    { cluster: 'محلی', words: [] }
  ],

  /* =========================================================================
   * SECTION D — SEMANTIC PROFILE BUILDER
   * ======================================================================= */
  classifyIntent(text, locations, industries) {
    const norm = this.normalizePersian(text);
    for (const s of this.intentSignals) {
      if (s.words.some(w => norm.includes(w))) return s.intent;
    }
    return locations.length > 0 ? 'location' : 'service';
  },

  extractMatches(text, dict) {
    const norm = this.normalizePersian(text);
    const found = [];
    dict.forEach(word => {
      const w = this.normalizePersian(word);
      if (w && norm.includes(w) && !found.includes(word)) found.push(word);
    });
    return found;
  },

  extractTopic(title, locations, stopWords) {
    let norm = this.normalizePersian(title);
    locations.forEach(loc => {
      const l = this.normalizePersian(loc);
      if (l) norm = norm.replace(new RegExp('\\b' + l + '\\b', 'g'), '');
    });
    const tokens = norm.split(' ').filter(w => w.length > 1 && !(stopWords || []).includes(w));
    return tokens.join(' ').trim();
  },

  buildProfile(item, stopWords, config) {
    const cfg = config || {};
    const locations = cfg.locations || this.defaultLocations;
    const industries = cfg.industries || this.defaultIndustries;
    const html = this.parseHtml(item.htmlContent);
    const title = item.title || '';
    const meta = item.meta_description || '';

    const matchedLocations = this.extractMatches(title + ' ' + meta, locations);
    const matchedIndustries = this.extractMatches(title + ' ' + meta + ' ' + html.headings.h2.join(' '), industries);
    const topic = this.extractTopic(title, matchedLocations, stopWords);

    const allHeadings = [...html.headings.h2, ...html.headings.h3, ...html.headings.h4];
    const subTopics = [...new Set(this.tokenize(allHeadings.join(' '), stopWords))].slice(0, 20);
    const entities = [...new Set([...matchedLocations, ...matchedIndustries])];

    const bodyText = [meta, ...html.paragraphs, ...html.listItems].join(' ');
    const wordCount = this.tokenize(bodyText, stopWords).length;
    const imageCount = (html.images || []).length;
    const imagesWithoutAlt = (html.images || []).filter(img => !img.alt || !img.alt.trim()).length;
    const tableCount = html.tableCount || 0;
    const listCount = html.listCount || 0;

    let contentDepth = 'thin';
    if (wordCount > 600 || allHeadings.length >= 6) contentDepth = 'deep';
    else if (wordCount > 200 || allHeadings.length >= 3) contentDepth = 'medium';

    const termVector = this.buildTermVector([
      { text: title, weight: 4 },
      { text: html.headings.h2.join(' '), weight: 2.2 },
      { text: meta, weight: 1.6 },
      { text: bodyText, weight: 1 }
    ], stopWords);

    // Keyword specificity: how many meaningful words the target keyword/title has.
    // A 3+ word keyword is usually long-tail => easier & faster to rank => higher
    // priority for quick SEO wins (used by the Action Plan).
    const keywordSource = item.keyword && item.keyword.trim() ? item.keyword : title;
    const keywordSpecificity = this.tokenize(keywordSource, stopWords).length;

    // Does the target keyword actually appear in the title / first heading?
    // (basic on-page relevance check, doesn't need a dictionary)
    const normKeyword = this.normalizePersian(keywordSource);
    const keywordInTitle = !!normKeyword && this.normalizePersian(title).includes(normKeyword);
    const keywordInHeadings = !!normKeyword && this.normalizePersian(allHeadings.join(' ')).includes(normKeyword);
    const keywordInMeta = !!normKeyword && this.normalizePersian(meta).includes(normKeyword);

    // Explicit category/tags, if provided by the caller — when present these
    // completely replace the automatic word-frequency guessing (far more
    // accurate than inferring topic groups from title text alone).
    const category = (item.category && String(item.category).trim()) || null;
    const tags = Array.isArray(item.tags) ? item.tags.map(t => String(t).trim()).filter(Boolean) : [];

    return {
      url: item.url,
      source: item.source,
      title,
      keyword: item.keyword || '',
      meta,
      category,
      tags,
      topic,
      subTopics,
      entities,
      locations: matchedLocations,
      industries: matchedIndustries,
      headings: html.headings,
      headingCount: allHeadings.length,
      anchorsOut: html.anchors,
      termVector,
      wordCount,
      imageCount,
      imagesWithoutAlt,
      tableCount,
      listCount,
      contentDepth,
      keywordSpecificity,
      keywordInTitle,
      keywordInHeadings,
      keywordInMeta,
      topicTokenCount: this.tokenize(topic, stopWords).length
    };
  },

  buildAllProfiles(items, stopWords, config) {
    return (items || []).map(item => this.buildProfile(item, stopWords, config));
  },

  /* =========================================================================
   * SECTION E — SEMANTIC SIMILARITY
   * ======================================================================= */
  semanticSimilarity(profileA, profileB, stopWords) {
    if (!profileA || !profileB) return 0;
    const titleSim = this.weightedCosine(
      this.buildTermVector([{ text: profileA.title, weight: 1 }], stopWords),
      this.buildTermVector([{ text: profileB.title, weight: 1 }], stopWords)
    );
    const bodySim = this.weightedCosine(profileA.termVector, profileB.termVector);
    const entitySim = this.jaccard(profileA.entities, profileB.entities);
    const score = titleSim * 0.40 + bodySim * 0.35 + entitySim * 0.25;
    return Math.round(Math.min(1, Math.max(0, score)) * 100);
  },

  /* =========================================================================
   * SECTION F — GRAPH ENGINE
   * ======================================================================= */
  // Strips protocol+domain (any domain — so it works regardless of which
  // site this runs on), URL-decodes percent-encoded slugs (Persian titles
  // in URLs come through as %DB%8C etc.), and drops query/hash/trailing
  // slash — so "https://orangesoft.ir/blog/%D8%B7...%D8%A8%D8%A7%D8%AF"
  // and "/blog/طراحی-سایت-در-نجف-آباد" normalize to the same key.
  normalizeUrlForMatch(url) {
    if (!url) return '';
    let u = String(url).trim();
    u = u.replace(/^https?:\/\/[^/]+/i, '');
    try { u = decodeURIComponent(u); } catch (e) { /* leave as-is if malformed encoding */ }
    u = u.split('#')[0].split('?')[0];
    if (u.length > 1) u = u.replace(/\/+$/, '');
    return u;
  },

  resolveUrl(url, urlSet, normalizedLookup) {
    if (!url) return null;
    if (urlSet.has(url)) return url; // fast path — already an exact match
    const norm = this.normalizeUrlForMatch(url);
    if (normalizedLookup && normalizedLookup[norm]) return normalizedLookup[norm];
    return null;
  },

  buildGraph(profiles, stopWords, threshold = 55) {
    const urlSet = new Set(profiles.map(p => p.url));
    const normalizedLookup = {};
    profiles.forEach(p => {
      normalizedLookup[this.normalizeUrlForMatch(p.url)] = p.url;
    });
    const nodes = {};

    profiles.forEach(p => {
      nodes[p.url] = {
        url: p.url,
        title: p.title,
        source: p.source,
        profile: p,
        incomingLinks: [],
        outgoingLinks: [],
        parents: [],
        children: [],
        siblings: [],
        isOrphan: false,
        isDeadEnd: false
      };
    });

    profiles.forEach(p => {
      const node = nodes[p.url];
      p.anchorsOut.forEach(a => {
        const resolved = this.resolveUrl(a.url, urlSet, normalizedLookup);
        if (resolved && resolved !== p.url) {
          node.outgoingLinks.push({ url: resolved, anchorText: a.text });
          nodes[resolved].incomingLinks.push({ url: p.url, anchorText: a.text });
        }
      });
    });

    for (let i = 0; i < profiles.length; i++) {
      for (let j = i + 1; j < profiles.length; j++) {
        const a = profiles[i];
        const b = profiles[j];
        const sim = this.semanticSimilarity(a, b, stopWords);
        if (sim < threshold) continue;

        if (a.topicTokenCount !== b.topicTokenCount) {
          const [parent, child] = a.topicTokenCount < b.topicTokenCount ? [a, b] : [b, a];
          nodes[child.url].parents.push({ url: parent.url, title: parent.title, similarity: sim });
          nodes[parent.url].children.push({ url: child.url, title: child.title, similarity: sim });
        } else {
          nodes[a.url].siblings.push({ url: b.url, title: b.title, similarity: sim });
          nodes[b.url].siblings.push({ url: a.url, title: a.title, similarity: sim });
        }
      }
    }

    Object.values(nodes).forEach(n => {
      ['parents', 'children', 'siblings'].forEach(key => {
        const map = {};
        n[key].forEach(r => { if (!map[r.url]) map[r.url] = r; });
        n[key] = Object.values(map).sort((x, y) => y.similarity - x.similarity);
      });
      n.isOrphan = n.incomingLinks.length === 0;
      n.isDeadEnd = n.outgoingLinks.length === 0;
    });

    return { nodes, urlSet };
  },

  /* =========================================================================
   * SECTION G — PAGERANK
   * ======================================================================= */
  computePageRank(graph, damping = 0.85, iterations = 30) {
    const urls = Object.keys(graph.nodes || {});
    const N = urls.length;
    if (N === 0) return { raw: {}, normalized: {} };

    let ranks = {};
    urls.forEach(u => { ranks[u] = 1 / N; });

    for (let it = 0; it < iterations; it++) {
      let danglingSum = 0;
      urls.forEach(u => {
        if (graph.nodes[u].outgoingLinks.length === 0) danglingSum += ranks[u];
      });
      const newRanks = {};
      urls.forEach(u => {
        let incomingSum = 0;
        graph.nodes[u].incomingLinks.forEach(inLink => {
          const src = graph.nodes[inLink.url];
          if (src && src.outgoingLinks.length > 0) incomingSum += ranks[inLink.url] / src.outgoingLinks.length;
        });
        newRanks[u] = (1 - damping) / N + damping * (incomingSum + danglingSum / N);
      });
      ranks = newRanks;
    }

    const max = Math.max(...Object.values(ranks), 0.00001);
    const normalized = {};
    urls.forEach(u => { normalized[u] = Math.round((ranks[u] / max) * 100); });
    return { raw: ranks, normalized };
  },

  /* =========================================================================
   * SECTION H — CLUSTERING & HUB DETECTION
   * ======================================================================= */
  assignCluster(profile, clusterMap) {
    const norm = this.normalizePersian(profile.title + ' ' + profile.topic);
    for (const entry of clusterMap) {
      if (entry.words.some(w => w && norm.includes(this.normalizePersian(w)))) return entry.cluster;
    }
    return profile.locations.length > 0 ? 'محلی' : 'سایر';
  },

  buildTopicalClusters(profiles, graph, pageRank, clusterMap) {
    const map = clusterMap || this.clusterKeywordMap;
    const clusters = {};
    profiles.forEach(p => {
      const clusterName = this.assignCluster(p, map);
      if (!clusters[clusterName]) clusters[clusterName] = { name: clusterName, pages: [] };
      clusters[clusterName].pages.push(p.url);
    });

    return Object.values(clusters).map(c => {
      const ranks = c.pages.map(u => pageRank.normalized[u] || 0);
      const avgAuthority = ranks.length ? Math.round(ranks.reduce((s, v) => s + v, 0) / ranks.length) : 0;
      return {
        name: c.name,
        pageCount: c.pages.length,
        pages: c.pages,
        avgAuthority,
        orphanCount: c.pages.filter(u => graph.nodes[u].isOrphan).length,
        deadEndCount: c.pages.filter(u => graph.nodes[u].isDeadEnd).length
      };
    }).sort((a, b) => b.pageCount - a.pageCount);
  },

  detectHubs(graph, pageRank, topicalClusters) {
    const hubs = [];
    topicalClusters.forEach(cluster => {
      if (cluster.pages.length < 2) return;
      let best = null;
      cluster.pages.forEach(url => {
        const node = graph.nodes[url];
        const score =
          node.incomingLinks.length * 2 +
          node.children.length * 3 +
          (pageRank.normalized[url] || 0) * 0.5 -
          node.profile.topicTokenCount * 4;
        if (!best || score > best.score) best = { url, score };
      });
      if (!best) return;

      const hubNode = graph.nodes[best.url];
      const expectedChildren = hubNode.children.map(c => c.url);
      const actualOutgoing = hubNode.outgoingLinks.map(l => l.url);
      const missingOutgoing = expectedChildren.filter(u => !actualOutgoing.includes(u));

      hubs.push({
        url: best.url,
        title: hubNode.title,
        cluster: cluster.name,
        hubScore: Math.min(100, Math.round(hubNode.incomingLinks.length * 2 + hubNode.outgoingLinks.length * 1.5 + hubNode.children.length * 3)),
        expectedOutgoing: expectedChildren.length,
        actualOutgoing: hubNode.outgoingLinks.length,
        missingOutgoing: missingOutgoing.map(u => ({ url: u, title: graph.nodes[u]?.title || u })),
        isDeadEndHub: hubNode.outgoingLinks.length === 0 && expectedChildren.length > 0
      });
    });
    return hubs.sort((a, b) => b.hubScore - a.hubScore);
  },

  detectSpokes(graph, hubs) {
    const hubUrlSet = new Set(hubs.map(h => h.url));
    const spokes = [];
    Object.values(graph.nodes).forEach(node => {
      if (hubUrlSet.has(node.url) || (node.parents.length === 0 && node.siblings.length === 0)) return;
      const parent = node.parents[0];
      const outUrls = node.outgoingLinks.map(l => l.url);
      if (parent && !outUrls.includes(parent.url)) {
        spokes.push({
          url: node.url,
          title: node.title,
          source: node.source,
          missingParent: true,
          parent
        });
      }
    });
    return spokes;
  },

  /* =========================================================================
   * SECTION I — ORPHANS & DEAD ENDS
   * ======================================================================= */
  // "Orphan" strictly means zero incoming links — a page can have low
  // PageRank authority for many legitimate reasons (small site, new page,
  // etc.) while still being properly linked. Weak-authority pages are a
  // separate concern already surfaced via each page's seoScore, so we
  // don't conflate the two here.
  detectOrphans(graph, pageRank) {
    return Object.values(graph.nodes)
      .filter(n => n.incomingLinks.length === 0)
      .map(n => ({
        url: n.url,
        title: n.title,
        source: n.source,
        incomingCount: n.incomingLinks.length,
        authority: pageRank.normalized[n.url] || 0
      }));
  },

  detectDeadEnds(graph) {
    return Object.values(graph.nodes)
      .filter(n => n.outgoingLinks.length === 0)
      .map(n => ({ url: n.url, title: n.title, source: n.source, incomingCount: n.incomingLinks.length }));
  },

  /* =========================================================================
   * SECTION J — ANCHOR ANALYSIS
   * ======================================================================= */
  analyzeAnchors(graph) {
    const results = [];
    Object.values(graph.nodes).forEach(node => {
      const anchors = node.incomingLinks.map(l => this.normalizePersian(l.anchorText || 'نامشخص'));
      if (anchors.length === 0) return;
      const freq = {};
      anchors.forEach(a => { freq[a] = (freq[a] || 0) + 1; });
      const total = anchors.length;
      const entries = Object.entries(freq).sort((a, b) => b[1] - a[1]);
      const [topAnchor, topCount] = entries[0] || ['', 0];
      const diversityScore = Math.round((Object.keys(freq).length / total) * 100);
      
      results.push({
        url: node.url,
        title: node.title,
        totalAnchors: total,
        topAnchor,
        topAnchorRatio: Math.round((topCount / total) * 100),
        diversityScore,
        overOptimized: topCount / total > 0.6 && total >= 3
      });
    });
    return results.sort((a, b) => a.diversityScore - b.diversityScore);
  },

  /* =========================================================================
   * SECTION K — MISSING LINKS & GAPS
   * ======================================================================= */
  buildMissingLinks(graph, hubs, spokes) {
    const suggestions = [];
    spokes.forEach(spoke => {
      if (spoke.parent) {
        suggestions.push({
          type: 'missingParent',
          sourceUrl: spoke.url,
          sourceTitle: spoke.title,
          targetUrl: spoke.parent.url,
          targetTitle: spoke.parent.title,
          similarity: spoke.parent.similarity || 0,
          reason: `شباهت معنایی ${spoke.parent.similarity || 0}٪ — والد موضوعی`
        });
      }
    });

    // Tag-based suggestions — only runs when the caller supplied `tags` on
    // items. Far more reliable than guessed similarity: if two articles
    // share an explicit tag and don't link to each other yet, that's a
    // near-certain internal-linking gap.
    const nodes = Object.values(graph.nodes);
    const hasTags = nodes.some(n => n.profile.tags && n.profile.tags.length > 0);
    if (hasTags) {
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const a = nodes[i], b = nodes[j];
          const tagsA = a.profile.tags || [];
          const tagsB = b.profile.tags || [];
          if (tagsA.length === 0 || tagsB.length === 0) continue;
          const shared = tagsA.filter(t => tagsB.includes(t));
          if (shared.length === 0) continue;

          const aLinksToB = a.outgoingLinks.some(l => l.url === b.url);
          const bLinksToA = b.outgoingLinks.some(l => l.url === a.url);

          if (!aLinksToB) {
            suggestions.push({
              type: 'missingTag',
              sourceUrl: a.url, sourceTitle: a.title,
              targetUrl: b.url, targetTitle: b.title,
              similarity: null,
              sharedTags: shared,
              reason: `برچسب مشترک: «${shared.join('، ')}» — این دو مقاله به هم لینک ندارند`
            });
          }
          if (!bLinksToA) {
            suggestions.push({
              type: 'missingTag',
              sourceUrl: b.url, sourceTitle: b.title,
              targetUrl: a.url, targetTitle: a.title,
              similarity: null,
              sharedTags: shared,
              reason: `برچسب مشترک: «${shared.join('، ')}» — این دو مقاله به هم لینک ندارند`
            });
          }
        }
      }
    }

    return suggestions;
  },

  /* =========================================================================
   * IRRELEVANT LINK DETECTION — flags EXISTING internal links that go to
   * topically unrelated pages (wastes link equity, confuses topical
   * relevance signals). Combined-signal approach:
   *  - If both pages have category/tags: flag only when there's NO
   *    category/tag overlap AND semantic similarity is low.
   *  - If category/tag data isn't available: fall back to semantic
   *    similarity alone, with a stricter threshold since it's the only
   *    signal.
   * Flagging only when both available signals agree keeps false positives
   * low (a link can be "on-topic" via tags even if wording looks distant).
   * ======================================================================= */
  detectIrrelevantLinks(graph, stopWords) {
    const semanticFloor = 25;   // below this = "not related" per content
    const strictSemanticFloor = 15; // used when category/tag data is unavailable
    const results = [];

    Object.values(graph.nodes).forEach(sourceNode => {
      sourceNode.outgoingLinks.forEach(link => {
        const targetNode = graph.nodes[link.url];
        if (!targetNode || targetNode.url === sourceNode.url) return;

        const sp = sourceNode.profile;
        const tp = targetNode.profile;
        const similarity = this.semanticSimilarity(sp, tp, stopWords);

        const hasCategoryTagData = !!((sp.category || (sp.tags && sp.tags.length)) && (tp.category || (tp.tags && tp.tags.length)));

        let categoryTagRelated = false;
        let sharedTags = [];
        if (hasCategoryTagData) {
          if (sp.category && tp.category && this.normalizePersian(sp.category) === this.normalizePersian(tp.category)) {
            categoryTagRelated = true;
          }
          sharedTags = (sp.tags || []).filter(t => (tp.tags || []).includes(t));
          if (sharedTags.length > 0) categoryTagRelated = true;
        }

        let isIrrelevant = false;
        let confidence = 'medium';
        if (hasCategoryTagData) {
          isIrrelevant = !categoryTagRelated && similarity < semanticFloor;
          confidence = (!categoryTagRelated && similarity < strictSemanticFloor) ? 'high' : 'medium';
        } else {
          isIrrelevant = similarity < strictSemanticFloor;
          confidence = 'medium'; // semantic-only, always medium since it's a single signal
        }

        if (isIrrelevant) {
          results.push({
            sourceUrl: sourceNode.url,
            sourceTitle: sourceNode.title,
            sourceCategory: sp.category,
            targetUrl: targetNode.url,
            targetTitle: targetNode.title,
            targetCategory: tp.category,
            anchorText: link.anchorText,
            similarity,
            confidence,
            reason: hasCategoryTagData
              ? `شباهت معنایی فقط ${similarity}٪ است و هیچ دسته/برچسب مشترکی با «${targetNode.title}» ندارد`
              : `شباهت معنایی این لینک با مقصد فقط ${similarity}٪ است`
          });
        }
      });
    });

    return results.sort((a, b) => (a.confidence === 'high' ? -1 : 1) - (b.confidence === 'high' ? -1 : 1));
  },

  /* =========================================================================
   * AUTOMATIC TOPIC-ROOT DISCOVERY (no hardcoded dictionaries)
   * -------------------------------------------------------------------------
   * Works for ANY site (medical clinics, perfume shops, real estate, ...).
   * Idea: tokens that appear in MANY titles across the site are "core" /
   * root words (e.g. "عطر", "طراحی", "سایت"). Tokens that appear in only a
   * FEW titles are "modifiers" — the thing that differentiates one page
   * from another (e.g. "زنانه", "پزشکی", "اصفهان"). Grouping pages by their
   * shared core tokens gives topic-root clusters automatically; comparing
   * the modifier vocabulary used by different clusters reveals gaps.
   * ======================================================================= */
  buildAutoTopicGroups(profiles, stopWords) {
    const N = profiles.length;
    if (N === 0) return {};

    const tokensByProfile = profiles.map(p => this.tokenize(p.title, stopWords));

    // Document frequency per token.
    const df = {};
    tokensByProfile.forEach(tokens => {
      new Set(tokens).forEach(t => { df[t] = (df[t] || 0) + 1; });
    });

    // Only tokens that repeat at least twice site-wide are candidates for
    // being part of a topic "root" — everything else is noise/one-off.
    const minSupport = 2;
    const candidateSet = new Set(Object.keys(df).filter(t => df[t] >= minSupport));

    // Pairwise co-occurrence: how many titles contain BOTH tokens together.
    // This is what lets us tell "عطر" + "اورجینال" (a true recurring root)
    // apart from "عطر" + "مردانه" (root + a modifier that only pairs with
    // "عطر" in this one title, not consistently).
    const pairCooc = {};
    const pairKey = (a, b) => (a < b ? a + '|' + b : b + '|' + a);
    tokensByProfile.forEach(tokens => {
      const cands = [...new Set(tokens)].filter(t => candidateSet.has(t));
      for (let i = 0; i < cands.length; i++) {
        for (let j = i + 1; j < cands.length; j++) {
          const k = pairKey(cands[i], cands[j]);
          pairCooc[k] = (pairCooc[k] || 0) + 1;
        }
      }
    });

    const groups = {};
    profiles.forEach((p, idx) => {
      const tokens = tokensByProfile[idx];
      const cands = tokens.filter(t => candidateSet.has(t)).sort((a, b) => df[b] - df[a]);

      // Greedily grow a root set: a token joins only if it consistently
      // co-occurs (>= minSupport titles) with every token already in root.
      const root = [];
      cands.forEach(tok => {
        const fitsAll = root.every(r => (pairCooc[pairKey(tok, r)] || 0) >= minSupport);
        if (fitsAll) root.push(tok);
      });
      const rootSet = new Set(root);
      const modifiers = tokens.filter(t => !rootSet.has(t));

      if (root.length === 0) return; // no recurring root — nothing to group on
      const key = [...root].sort().join(' ');
      if (!groups[key]) groups[key] = { root: key, pages: [], modifiersUsed: new Set(), modifierByPage: {} };
      groups[key].pages.push(p.url);
      groups[key].modifierByPage[p.url] = modifiers;
      modifiers.forEach(m => groups[key].modifiersUsed.add(m));
    });

    return groups;
  },

  /* =========================================================================
   * CATEGORY/TAG-BASED TOPIC GROUPING (preferred when the caller supplies
   * explicit `category` + `tags` per item — far more accurate than guessing
   * from title word-frequency, since there's nothing to guess anymore).
   * ======================================================================= */
  buildCategoryTopicGroups(profiles) {
    const groups = {};
    profiles.forEach(p => {
      if (!p.category) return;
      const key = this.normalizePersian(p.category);
      if (!groups[key]) groups[key] = { root: p.category, pages: [], modifiersUsed: new Set(), modifierByPage: {} };
      groups[key].pages.push(p.url);
      groups[key].modifierByPage[p.url] = p.tags || [];
      (p.tags || []).forEach(t => groups[key].modifiersUsed.add(t));
    });
    return groups;
  },

  // Aggregate profile per existing category — used to suggest a category
  // for pages that don't have one yet, and to flag likely mis-categorized
  // pages (current category scores far below the best alternative).
  buildCategoryProfiles(profiles) {
    const byCategory = {};
    profiles.forEach(p => {
      if (!p.category) return;
      const key = this.normalizePersian(p.category);
      if (!byCategory[key]) byCategory[key] = { category: p.category, tagSet: new Set(), termVector: {}, memberUrls: [] };
      (p.tags || []).forEach(t => byCategory[key].tagSet.add(t));
      byCategory[key].memberUrls.push(p.url);
      Object.entries(p.termVector).forEach(([term, weight]) => {
        byCategory[key].termVector[term] = (byCategory[key].termVector[term] || 0) + weight;
      });
    });
    return byCategory;
  },

  suggestCategoryForPage(profile, categoryProfiles) {
    const entries = Object.values(categoryProfiles);
    if (entries.length === 0) return null;

    let best = null;
    entries.forEach(cat => {
      const tagOverlap = this.jaccard(profile.tags || [], [...cat.tagSet]);
      const contentSim = this.weightedCosine(profile.termVector, cat.termVector);
      const score = Math.round((tagOverlap * 0.6 + contentSim * 0.4) * 100);
      if (!best || score > best.score) best = { category: cat.category, score };
    });
    return best;
  },


  buildKeywordGaps(profiles, stopWords, gapDictionary) {
    // Prefer explicit category/tag data when the caller supplied it for at
    // least half the pages — no guessing needed, and it's simply more
    // accurate. Otherwise fall back to automatic word-frequency discovery.
    const withCategory = profiles.filter(p => p.category).length;
    const useCategoryGroups = profiles.length > 0 && withCategory / profiles.length >= 0.5;

    const groups = useCategoryGroups
      ? this.buildCategoryTopicGroups(profiles)
      : this.buildAutoTopicGroups(profiles, stopWords);
    const groupList = Object.values(groups).filter(g => g.pages.length >= 2);
    if (groupList.length < 2) return []; // need at least 2 groups to compare against each other

    // Every modifier used ANYWHERE on the site is a candidate — a modifier
    // seen in just 1 other group is still a real, site-specific signal
    // (e.g. "بچگانه" used for عطر but not yet for اسپری). We don't require a
    // fixed dictionary; we just require the word to have already proven
    // itself useful somewhere else on the same site. Modifiers seen in 2+
    // groups get marked "قطعی" (high confidence), seen in exactly 1 group
    // get marked "احتمالی" (worth checking) — both are shown, just labeled.
    const modifierGroupMap = {};
    groupList.forEach(g => {
      g.modifiersUsed.forEach(m => {
        if (!modifierGroupMap[m]) modifierGroupMap[m] = new Set();
        modifierGroupMap[m].add(g.root);
      });
    });

    // A word that is itself the root of another group (e.g. "مردانه" ended
    // up as its own topic-root because of a clustering tie) shouldn't also
    // be suggested as a modifier — combining two root-like words produces
    // nonsense titles like "مردانه مردانه". Filter those out.
    const allRootTokens = new Set();
    groupList.forEach(g => g.root.split(' ').forEach(t => allRootTokens.add(t)));

    const gaps = [];
    groupList.forEach(g => {
      const gRootTokens = new Set(g.root.split(' '));
      Object.entries(modifierGroupMap).forEach(([mod, groupsWithMod]) => {
        if (allRootTokens.has(mod)) return;         // mod is itself a root elsewhere — skip
        if (gRootTokens.has(mod)) return;            // mod already part of this root — skip
        if (g.modifiersUsed.has(mod)) return;        // already covered here
        if (groupsWithMod.has(g.root)) return;        // safety, shouldn't happen
        const usedInGroups = groupsWithMod.size;
        const confidence = usedInGroups >= 2 ? 'high' : 'medium';
        gaps.push({
          type: 'auto',
          confidence,
          topic: g.root,
          modifier: mod,
          suggestedTitle: `${g.root} ${mod}`.trim(),
          targetKeyword: `${g.root} ${mod}`.trim(),
          pageCount: g.pages.length,
          reason: usedInGroups >= 2
            ? `کلمه‌ی «${mod}» در ${usedInGroups} خوشه‌ی دیگر سایت شما به‌کار رفته، اما برای «${g.root}» صفحه‌ای با این کلمه ندارید`
            : `کلمه‌ی «${mod}» فقط برای یک خوشه‌ی دیگر استفاده شده؛ احتمالاً برای «${g.root}» هم قابل استفاده است`
        });
      });
    });

    // Optional manual supplement — if the user explicitly passes a dictionary,
    // merge it in too (kept for backward compatibility / manual override).
    if (Array.isArray(gapDictionary) && gapDictionary.length) {
      groupList.forEach(g => {
        gapDictionary.forEach(mod => {
          const already = g.modifiersUsed.has(mod) || gaps.some(x => x.topic === g.root && x.modifier === mod);
          if (!already) {
            gaps.push({
              type: 'manual',
              confidence: 'high',
              topic: g.root,
              modifier: mod,
              suggestedTitle: `${g.root} ${mod}`.trim(),
              targetKeyword: `${g.root} ${mod}`.trim(),
              pageCount: g.pages.length,
              reason: `طبق فهرست سفارشی شما، «${mod}» برای «${g.root}» هنوز پوشش داده نشده`
            });
          }
        });
      });
    }

    // Deduplicate + sort: high-confidence gaps first, then by how
    // established the topic root is (more pages = more worth expanding).
    const seen = new Set();
    return gaps
      .filter(g => {
        if (seen.has(g.targetKeyword)) return false;
        seen.add(g.targetKeyword);
        return true;
      })
      .sort((a, b) => {
        if (a.confidence !== b.confidence) return a.confidence === 'high' ? -1 : 1;
        return b.pageCount - a.pageCount;
      });
  },

  /* =========================================================================
   * SECTION L — SCORING
   * ======================================================================= */
  /* =========================================================================
   * CONTENT QUALITY SCORE — the part that was previously missing entirely.
   * This is computed straight from the HTML (word count, headings, images,
   * tables, lists, on-page keyword presence) and now genuinely feeds into
   * the final seoScore below, instead of only appearing as advice text.
   * ======================================================================= */
  computeContentQualityScore(profile) {
    // Word count (0-100): thin content is penalized hard, 600+ words is full marks.
    let wordScore;
    if (profile.wordCount >= 1200) wordScore = 100;
    else if (profile.wordCount >= 600) wordScore = 90;
    else if (profile.wordCount >= 300) wordScore = 65;
    else if (profile.wordCount >= 150) wordScore = 35;
    else wordScore = 10;

    // Headings (0-100): structure matters for both readability and SEO.
    let headingScore;
    if (profile.headingCount >= 6) headingScore = 100;
    else if (profile.headingCount >= 3) headingScore = 75;
    else if (profile.headingCount >= 1) headingScore = 40;
    else headingScore = 0;

    // Images (0-100), penalized for missing alt text (accessibility + image SEO).
    let imageScore;
    if (profile.imageCount >= 3) imageScore = 100;
    else if (profile.imageCount >= 1) imageScore = 60;
    else imageScore = 0;
    const altPenalty = Math.min(40, profile.imagesWithoutAlt * 15);
    imageScore = Math.max(0, imageScore - altPenalty);

    // Structural richness bonus: tables / lists help both users and rich results.
    const structureBonus = Math.min(20, (profile.tableCount > 0 ? 10 : 0) + (profile.listCount > 0 ? 10 : 0));

    // On-page keyword relevance: does the target keyword actually show up
    // in the places search engines weight most (title, headings, meta)?
    const relevanceHits = [profile.keywordInTitle, profile.keywordInHeadings, profile.keywordInMeta].filter(Boolean).length;
    const relevanceScore = profile.keyword ? Math.round((relevanceHits / 3) * 100) : 60; // neutral if no target keyword given

    const score = Math.round(
      wordScore * 0.32 +
      headingScore * 0.20 +
      imageScore * 0.20 +
      relevanceScore * 0.18 +
      structureBonus * 0.5 // structureBonus is already 0-20, so *0.5 caps its contribution at 10 points
    );

    return {
      score: Math.min(100, Math.max(0, score)),
      breakdown: { wordScore, headingScore, imageScore, structureBonus, relevanceScore }
    };
  },

  buildPageScores(graph, pageRank, hubs, anchorAnalysis) {
    const hubUrlSet = new Set(hubs.map(h => h.url));
    const hubByUrl = {};
    hubs.forEach(h => { hubByUrl[h.url] = h; });
    const anchorByUrl = {};
    anchorAnalysis.forEach(a => { anchorByUrl[a.url] = a; });

    const scores = {};
    Object.values(graph.nodes).forEach(node => {
      const authorityScore = pageRank.normalized[node.url] || 0;
      const hubScore = hubUrlSet.has(node.url) ? hubByUrl[node.url].hubScore : Math.max(0, 40 - node.profile.topicTokenCount * 5);
      const anchorInfo = anchorByUrl[node.url];
      const anchorScore = anchorInfo ? (anchorInfo.diversityScore - (anchorInfo.overOptimized ? 20 : 0)) : 0;
      const linkScore = Math.min(100, node.incomingLinks.length * 10 + node.outgoingLinks.length * 8);
      const contentQuality = this.computeContentQualityScore(node.profile);

      // Content quality now carries the largest single weight — a page with
      // great links but thin, image-less content can no longer score "good".
      const seoScore = Math.round(
        contentQuality.score * 0.35 +
        authorityScore * 0.18 +
        linkScore * 0.20 +
        hubScore * 0.12 +
        Math.max(0, anchorScore) * 0.15
      );

      scores[node.url] = {
        seoScore: Math.min(100, Math.max(0, seoScore)),
        authorityScore,
        hubScore: Math.round(hubScore),
        anchorScore: Math.round(Math.max(0, anchorScore)),
        internalLinkingScore: linkScore,
        contentQualityScore: contentQuality.score,
        contentQualityBreakdown: contentQuality.breakdown
      };
    });
    return scores;
  },

  /* =========================================================================
   * SECTION M — ACTION PLAN (§ "SEO Assistant")
   * -------------------------------------------------------------------------
   * Turns raw scores into a prioritized, human-readable to-do list:
   *  1. WHICH page to fix first (low score + long-tail keyword = quick win)
   *  2. WHY it's low (thin content? no images? weak internal links?)
   *  3. WHAT to literally do about it (concrete, actionable steps)
   *  4. A separate, sequenced internal-linking plan (hub/parent links first,
   *     then contextual links) built from the missing-link suggestions.
   * ======================================================================= */
  buildActionPlan(graph, pageScores, anchorAnalysis, missingLinks, hubs, irrelevantLinks) {
    const anchorByUrl = {};
    anchorAnalysis.forEach(a => { anchorByUrl[a.url] = a; });
    const missingByUrl = {};
    missingLinks.forEach(m => {
      if (!missingByUrl[m.sourceUrl]) missingByUrl[m.sourceUrl] = [];
      missingByUrl[m.sourceUrl].push(m);
    });
    const irrelevantByUrl = {};
    (irrelevantLinks || []).forEach(l => {
      if (!irrelevantByUrl[l.sourceUrl]) irrelevantByUrl[l.sourceUrl] = [];
      irrelevantByUrl[l.sourceUrl].push(l);
    });
    const hubUrlSet = new Set(hubs.map(h => h.url));

    const pageActions = Object.values(graph.nodes).map(node => {
      const p = node.profile;
      const score = pageScores[node.url] || { seoScore: 0, internalLinkingScore: 0 };
      const anchorInfo = anchorByUrl[node.url];
      const missingForPage = missingByUrl[node.url] || [];
      const irrelevantForPage = irrelevantByUrl[node.url] || [];

      const actions = [];

      if (p.contentDepth === 'thin') {
        actions.push({
          icon: '📝',
          text: `طول محتوا کم است (${p.wordCount} کلمه). حداقل به ۶۰۰ کلمه برسانید و توضیحات کامل‌تری اضافه کنید.`
        });
      } else if (p.contentDepth === 'medium') {
        actions.push({
          icon: '📝',
          text: `محتوا متوسط است (${p.wordCount} کلمه). برای رقابت بهتر، عمق محتوا را افزایش دهید.`
        });
      }

      if (p.headingCount < 3) {
        actions.push({
          icon: '🏷️',
          text: `تعداد تیترها کم است (${p.headingCount} تیتر). حداقل ۳ تا ۵ تیتر H2/H3 برای ساختاردهی بهتر اضافه کنید.`
        });
      }

      if (p.imageCount === 0) {
        actions.push({
          icon: '🖼️',
          text: 'هیچ تصویری در این صفحه وجود ندارد. حداقل یک تصویر مرتبط با alt text مناسب اضافه کنید.'
        });
      } else if (p.imagesWithoutAlt > 0) {
        actions.push({
          icon: '🖼️',
          text: `${p.imagesWithoutAlt} تصویر بدون alt text دارید. برای سئوی تصویر، alt text توصیفی اضافه کنید.`
        });
      }

      if (anchorInfo && anchorInfo.overOptimized) {
        actions.push({
          icon: '⚓',
          text: `انکرتکست‌های ورودی این صفحه بیش‌بهینه شده‌اند (${anchorInfo.topAnchorRatio}% یکسان). تنوع بدهید.`
        });
      }

      if (score.internalLinkingScore < 40) {
        actions.push({
          icon: '🔗',
          text: `لینک‌دهی داخلی این صفحه ضعیف است (${node.incomingLinks.length} ورودی، ${node.outgoingLinks.length} خروجی).`
        });
      }

      missingForPage.slice(0, 3).forEach(m => {
        actions.push({
          icon: '➕',
          text: `لینک به «${m.targetTitle}» اضافه کنید — ${m.reason}`
        });
      });

      irrelevantForPage.slice(0, 3).forEach(l => {
        actions.push({
          icon: '🚫',
          text: `لینک به «${l.targetTitle}» را حذف یا اصلاح کنید — ${l.reason}`
        });
      });

      if (node.isOrphan) {
        actions.push({ icon: '🏝️', text: 'این صفحه هیچ لینک ورودی ندارد؛ از صفحات مرتبط به آن لینک دهید.' });
      }
      if (node.isDeadEnd) {
        actions.push({ icon: '🚧', text: 'این صفحه هیچ لینک خروجی ندارد؛ به صفحات مرتبط لینک دهید.' });
      }

      // Priority: low score = more urgent; long-tail (more specific) keywords
      // get a boost because they're typically faster/easier wins.
      const specificityBoost = 1 + Math.min(0.6, Math.max(0, p.keywordSpecificity - 1) * 0.15);
      const priorityScore = Math.round((100 - score.seoScore) * specificityBoost);

      return {
        url: node.url,
        title: node.title,
        source: node.source,
        seoScore: score.seoScore,
        keywordSpecificity: p.keywordSpecificity,
        isHub: hubUrlSet.has(node.url),
        priorityScore,
        actions
      };
    });

    const contentPlan = pageActions
      .filter(pa => pa.actions.length > 0)
      .sort((a, b) => b.priorityScore - a.priorityScore)
      .slice(0, 20);

    // Sequenced link-building plan: fix structural links (parent/hub) before
    // purely contextual ones — structural links carry more authority.
    const priorityOrder = { missingHub: 0, missingParent: 1, missingChild: 1, missingSibling: 2, missingContextual: 3 };
    const linkBuildingPlan = [...missingLinks].sort((a, b) => {
      const pa = priorityOrder[a.type] ?? 4;
      const pb = priorityOrder[b.type] ?? 4;
      if (pa !== pb) return pa - pb;
      return (b.similarity || 0) - (a.similarity || 0);
    });

    return { contentPlan, linkBuildingPlan };
  },

  /* =========================================================================
   * ORCHESTRATOR
   * ======================================================================= */
  /* =========================================================================
   * SECTION N — PER-ARTICLE FULL REPORT
   * -------------------------------------------------------------------------
   * One self-contained object per page: scores + breakdown, content
   * diagnostics checklist, outgoing link suggestions, category suggestion,
   * and prioritized actions. This is what powers the "article-by-article"
   * tab AND the JSON export — every page reviewed from every angle.
   * ======================================================================= */
  buildArticleReports(graph, pageScores, anchorAnalysis, missingLinks, categoryProfiles, actionPlan, irrelevantLinks) {
    const anchorByUrl = {};
    anchorAnalysis.forEach(a => { anchorByUrl[a.url] = a; });
    const missingByUrl = {};
    missingLinks.forEach(m => {
      if (!missingByUrl[m.sourceUrl]) missingByUrl[m.sourceUrl] = [];
      missingByUrl[m.sourceUrl].push(m);
    });
    const actionsByUrl = {};
    (actionPlan.contentPlan || []).forEach(pa => { actionsByUrl[pa.url] = pa.actions; });
    const irrelevantByUrl = {};
    (irrelevantLinks || []).forEach(l => {
      if (!irrelevantByUrl[l.sourceUrl]) irrelevantByUrl[l.sourceUrl] = [];
      irrelevantByUrl[l.sourceUrl].push(l);
    });

    return Object.values(graph.nodes).map(node => {
      const p = node.profile;
      const score = pageScores[node.url] || {};
      const anchorInfo = anchorByUrl[node.url] || null;

      const categorySuggestion = !p.category
        ? this.suggestCategoryForPage(p, categoryProfiles)
        : null;

      return {
        url: node.url,
        title: node.title,
        source: node.source,
        category: p.category,
        tags: p.tags,
        keyword: p.keyword,

        scores: {
          seoScore: score.seoScore || 0,
          contentQualityScore: score.contentQualityScore || 0,
          authorityScore: score.authorityScore || 0,
          internalLinkingScore: score.internalLinkingScore || 0,
          anchorScore: score.anchorScore || 0,
          hubScore: score.hubScore || 0,
          breakdown: score.contentQualityBreakdown || {}
        },

        contentDiagnostics: {
          wordCount: p.wordCount,
          headingCount: p.headingCount,
          imageCount: p.imageCount,
          imagesWithoutAlt: p.imagesWithoutAlt,
          tableCount: p.tableCount,
          listCount: p.listCount,
          contentDepth: p.contentDepth,
          keywordInTitle: p.keywordInTitle,
          keywordInHeadings: p.keywordInHeadings,
          keywordInMeta: p.keywordInMeta
        },

        linkStatus: {
          incomingCount: node.incomingLinks.length,
          outgoingCount: node.outgoingLinks.length,
          isOrphan: node.isOrphan,
          isDeadEnd: node.isDeadEnd,
          isHub: node.isHub
        },

        anchorDiversity: anchorInfo ? {
          diversityScore: anchorInfo.diversityScore,
          overOptimized: anchorInfo.overOptimized,
          topAnchor: anchorInfo.topAnchor
        } : null,

        linkSuggestions: (missingByUrl[node.url] || []).map(m => ({
          type: m.type,
          targetUrl: m.targetUrl,
          targetTitle: m.targetTitle,
          reason: m.reason,
          similarity: m.similarity
        })),

        irrelevantOutgoingLinks: (irrelevantByUrl[node.url] || []).map(l => ({
          targetUrl: l.targetUrl,
          targetTitle: l.targetTitle,
          anchorText: l.anchorText,
          similarity: l.similarity,
          confidence: l.confidence,
          reason: l.reason
        })),

        categorySuggestion,

        actions: actionsByUrl[node.url] || []
      };
    }).sort((a, b) => a.scores.seoScore - b.scores.seoScore); // worst first
  },

  // Clean, self-contained JSON export — safe to JSON.stringify() directly
  // (no Sets, no circular refs). Meant to be downloaded/copied and pasted
  // back into a chat for further help.
  /* =========================================================================
   * AI-ASSISTED CONTENT ANALYSIS EXPORT
   * -------------------------------------------------------------------------
   * Word-frequency heuristics are fine for structural facts (links, orphans,
   * word count) but genuinely bad at judging MEANING — that's why the old
   * automatic keyword-gap detector produced junk like "معرفی معرفی". Rather
   * than fake semantic understanding with more heuristics, this exports a
   * clean, minimal structure (title/H2/H3/meta/category/tags per page) that
   * an LLM can actually read and reason about for content gaps and keyword
   * cannibalization — a task that genuinely needs language understanding.
   * ======================================================================= */
  buildContentStructureExport(profiles, graph, pageScores) {
    return profiles.map(p => {
      const node = graph ? graph.nodes[p.url] : null;
      const incomingLinks = node ? node.incomingLinks.map(l => ({
        fromUrl: l.url,
        fromTitle: graph.nodes[l.url] ? graph.nodes[l.url].title : l.url,
        anchorText: l.anchorText
      })) : [];
      const outgoingLinks = node ? node.outgoingLinks.map(l => ({
        toUrl: l.url,
        toTitle: graph.nodes[l.url] ? graph.nodes[l.url].title : l.url,
        anchorText: l.anchorText
      })) : [];
      const score = pageScores ? pageScores[p.url] : null;

      return {
        url: p.url,
        title: p.title,
        keyword: p.keyword || null,
        metaDescription: p.meta || null,
        category: p.category || null,
        tags: p.tags || [],
        h2: p.headings.h2,
        h3: p.headings.h3,
        wordCount: p.wordCount,
        // Signals for deciding WHICH article should survive a merge —
        // higher seoScore/authorityScore/incoming-link-count = stronger
        // candidate to keep; the weaker one is the merge/redirect target.
        seoScore: score ? score.seoScore : null,
        authorityScore: score ? score.authorityScore : null,
        incomingLinkCount: incomingLinks.length,
        incomingLinks,
        outgoingLinks
      };
    });
  },

  buildExportJSON(analysis) {
    const pla = analysis.productLinkAnalysis || { products: [], orphanProducts: [], articlesWithNoProductLinks: [], suggestions: [] };
    return {
      generatedAt: new Date().toISOString(),
      summary: {
        totalPages: analysis.profiles.length,
        totalClusters: analysis.topicalClusters.length,
        totalHubs: analysis.hubs.length,
        totalOrphans: analysis.orphans.length,
        totalDeadEnds: analysis.deadEnds.length,
        totalMissingLinks: analysis.missingLinks.length,
        totalIrrelevantLinks: (analysis.irrelevantLinks || []).length,
        totalKeywordGaps: analysis.keywordGaps.length,
        totalProducts: pla.products.length,
        totalOrphanProducts: pla.orphanProducts.length,
        totalArticlesWithNoProductLinks: pla.articlesWithNoProductLinks.length,
        totalProductLinkSuggestions: pla.suggestions.length,
        avgSeoScore: analysis.articleReports.length
          ? Math.round(analysis.articleReports.reduce((s, a) => s + a.scores.seoScore, 0) / analysis.articleReports.length)
          : 0
      },
      topicalClusters: analysis.topicalClusters,
      keywordGaps: analysis.keywordGaps,
      linkBuildingPlan: analysis.actionPlan.linkBuildingPlan,
      irrelevantLinks: analysis.irrelevantLinks || [],
      productLinkAnalysis: pla,
      contentStructure: this.buildContentStructureExport(analysis.profiles, analysis.graph, analysis.pageScores),
      articles: analysis.articleReports
    };
  },

  /* =========================================================================
   * ARTICLE ↔ PRODUCT LINKING (lightweight, separate layer)
   * -------------------------------------------------------------------------
   * Deliberately NOT folded into the main graph/PageRank/hub-detection
   * pipeline — those concepts (topical hubs, spokes, clusters) don't map
   * cleanly onto product pages, and mixing them in would make the whole
   * engine harder to reason about for little benefit. Instead: a small,
   * focused pass that just answers "which products get linked from which
   * articles, and where are the gaps?"
   * ======================================================================= */
  detectItemType(item, config) {
    if (item.type === 'product' || item.type === 'article') return item.type;
    const prefix = (config && config.productUrlPrefix) || '/product';
    return item.url && item.url.startsWith(prefix) ? 'product' : 'article';
  },

  buildProductLinkAnalysis(articleProfiles, productProfiles, stopWords) {
    const allUrls = new Set([
      ...articleProfiles.map(p => p.url),
      ...productProfiles.map(p => p.url)
    ]);
    const normalizedLookup = {};
    allUrls.forEach(u => { normalizedLookup[this.normalizeUrlForMatch(u)] = u; });

    const productByUrl = {};
    productProfiles.forEach(p => { productByUrl[p.url] = p; });

    const productIncoming = {};
    productProfiles.forEach(p => { productIncoming[p.url] = []; });
    const articleOutgoingProducts = {};
    articleProfiles.forEach(p => { articleOutgoingProducts[p.url] = []; });

    articleProfiles.forEach(article => {
      article.anchorsOut.forEach(a => {
        const resolved = this.resolveUrl(a.url, allUrls, normalizedLookup);
        if (resolved && productByUrl[resolved]) {
          productIncoming[resolved].push({ fromUrl: article.url, fromTitle: article.title, anchorText: a.text });
          articleOutgoingProducts[article.url].push({ toUrl: resolved, toTitle: productByUrl[resolved].title, anchorText: a.text });
        }
      });
    });

    const products = productProfiles.map(p => ({
      url: p.url,
      title: p.title,
      category: p.category,
      tags: p.tags,
      incomingArticleLinks: productIncoming[p.url],
      incomingCount: productIncoming[p.url].length,
      isOrphanProduct: productIncoming[p.url].length === 0
    }));

    const articlesWithNoProductLinks = articleProfiles
      .filter(a => articleOutgoingProducts[a.url].length === 0)
      .map(a => ({ url: a.url, title: a.title, source: a.source }));

    // Suggest article -> product links based on shared category/tags (the
    // same reliable signal used for article-to-article tag-based linking).
    const suggestions = [];
    if (productProfiles.length > 0) {
      articleProfiles.forEach(article => {
        const linkedProductUrls = new Set(articleOutgoingProducts[article.url].map(l => l.toUrl));
        const candidates = [];
        productProfiles.forEach(product => {
          if (linkedProductUrls.has(product.url)) return;
          const sharedTags = (article.tags || []).filter(t => (product.tags || []).includes(t));
          const categoryMatch = article.category && product.category &&
            this.normalizePersian(article.category) === this.normalizePersian(product.category);
          if (sharedTags.length === 0 && !categoryMatch) return;
          candidates.push({
            articleUrl: article.url,
            articleTitle: article.title,
            articleSource: article.source,
            productUrl: product.url,
            productTitle: product.title,
            sharedTags,
            categoryMatch,
            reason: categoryMatch
              ? `مقاله و محصول هر دو در دسته‌ی «${article.category}» هستند`
              : `برچسب مشترک: «${sharedTags.join('، ')}»`
          });
        });
        candidates
          .sort((a, b) => (b.sharedTags.length + (b.categoryMatch ? 1 : 0)) - (a.sharedTags.length + (a.categoryMatch ? 1 : 0)))
          .slice(0, 3)
          .forEach(c => suggestions.push(c));
      });
    }

    return {
      products,
      orphanProducts: products.filter(p => p.isOrphanProduct),
      articlesWithNoProductLinks,
      suggestions
    };
  },

  analyze(items, stopWords, config = {}) {
    const articleItems = items.filter(i => this.detectItemType(i, config) === 'article');
    const productItems = items.filter(i => this.detectItemType(i, config) === 'product');

    const profiles = this.buildAllProfiles(articleItems, stopWords, config);
    const graph = this.buildGraph(profiles, stopWords, config.familyThreshold || 55);
    const pageRank = this.computePageRank(graph);
    const topicalClusters = this.buildTopicalClusters(profiles, graph, pageRank, config.clusterMap);
    const hubs = this.detectHubs(graph, pageRank, topicalClusters);
    const spokes = this.detectSpokes(graph, hubs);
    const orphans = this.detectOrphans(graph, pageRank);
    const deadEnds = this.detectDeadEnds(graph);
    const anchorAnalysis = this.analyzeAnchors(graph);
    const missingLinks = this.buildMissingLinks(graph, hubs, spokes);
    const irrelevantLinks = this.detectIrrelevantLinks(graph, stopWords);
    const keywordGaps = this.buildKeywordGaps(profiles, stopWords, config.gapDictionary);
    const pageScores = this.buildPageScores(graph, pageRank, hubs, anchorAnalysis);
    const actionPlan = this.buildActionPlan(graph, pageScores, anchorAnalysis, missingLinks, hubs, irrelevantLinks);
    const categoryProfiles = this.buildCategoryProfiles(profiles);
    const articleReports = this.buildArticleReports(graph, pageScores, anchorAnalysis, missingLinks, categoryProfiles, actionPlan, irrelevantLinks);

    const productProfiles = this.buildAllProfiles(productItems, stopWords, config);
    const productLinkAnalysis = this.buildProductLinkAnalysis(profiles, productProfiles, stopWords);

    const result = {
      profiles,
      graph,
      pageRank,
      topicalClusters,
      hubs,
      spokes,
      orphans,
      deadEnds,
      anchorAnalysis,
      missingLinks,
      irrelevantLinks,
      keywordGaps,
      pageScores,
      actionPlan,
      articleReports,
      productLinkAnalysis
    };
    result.exportJSON = this.buildExportJSON(result);
    return result;
  }
};

export default SeoEngine;
